// WhatsApp Bot Chat Service
// Handles communication between PWA and Aaron's WhatsApp Bot
// Based on WA_BOT_IMPLEMENTATIONPLAN.md and aaron/N8N_AND_FRONTEND_INTEGRATION.md

import { supabase } from './supabase'

// Chat message interface matching the bot's message structure
export interface ChatMessage {
  id: string
  conversation_id: string
  user_id?: string
  phone_number: string
  message_type: 'text' | 'image' | 'document' | 'template' | 'interactive'
  content: string
  direction: 'inbound' | 'outbound'
  whatsapp_message_id?: string
  status: 'sent' | 'delivered' | 'read' | 'failed'
  metadata?: Record<string, any>
  created_at: string
}

// Chat conversation interface
export interface ChatConversation {
  id: string
  user_id: string
  phone_number: string
  status: 'active' | 'inactive' | 'blocked'
  last_message_at: string
  metadata?: Record<string, any>
  created_at: string
  updated_at: string
}

// Bot webhook payload interface (from n8n integration docs)
export interface BotWebhookPayload {
  phoneNumber: string
  userMessage: string
  userId?: string | null
  historyText?: string
}

// Bot response interface
export interface BotResponse {
  success: boolean
  message?: string
  error?: string
  conversationId?: string
}

// Chat service configuration
const BOT_CONFIG = {
  webhookUrl: import.meta.env.VITE_WHATSAPP_BOT_WEBHOOK_URL || 'http://134.209.23.94/webhook',
  adminUrl: import.meta.env.VITE_WHATSAPP_BOT_ADMIN_URL || 'https://tyrelink.co.za/admin',
  phoneNumber: import.meta.env.VITE_WHATSAPP_PHONE_NUMBER || '+27686247270',
  internalToken: import.meta.env.VITE_WHATSAPP_BOT_INTERNAL_TOKEN,
}

export class ChatService {
  private connected = false
  private messageCallbacks: Set<(message: ChatMessage) => void> = new Set()
  private typingCallbacks: Set<(userId: string) => void> = new Set()
  private userId: string | null = null
  private conversationId: string | null = null

  // Initialize chat service with user authentication
  async connect(userId: string): Promise<void> {
    try {
      this.userId = userId
      
      // Get or create user conversation
      const conversation = await this.getOrCreateConversation(userId)
      this.conversationId = conversation.id
      
      // Set up real-time message subscription
      await this.setupRealtimeSubscription()
      
      this.connected = true
    } catch (error) {
      console.error('Failed to connect chat service:', error)
      throw new Error('Failed to connect to chat service')
    }
  }

  // Send message to WhatsApp bot
  async sendMessage(message: string, userId: string): Promise<BotResponse> {
    if (!this.connected) {
      throw new Error('Chat service not connected')
    }

    try {
      // Get user profile for phone number
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('whatsapp_number, phone')
        .eq('id', userId)
        .single()

      const phoneNumber = profile?.whatsapp_number || profile?.phone
      if (!phoneNumber) {
        throw new Error('User phone number not found')
      }

      // Get recent message history for context
      const historyText = await this.getRecentMessageHistory(userId, 5)

      // Prepare webhook payload
      const payload: BotWebhookPayload = {
        phoneNumber: 'web-user',
        userMessage: message,
        userId: userId,
        historyText
      }

      // Send to bot webhook
      const response = await fetch(BOT_CONFIG.webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(BOT_CONFIG.internalToken && {
            'x-internal-token': BOT_CONFIG.internalToken
          })
        },
        body: JSON.stringify(payload)
      })

      if (!response.ok) {
        throw new Error(`Bot webhook failed: ${response.status}`)
      }

      // Store outbound message in database
      await this.storeMessage({
        conversation_id: this.conversationId!,
        user_id: userId,
        phone_number: phoneNumber,
        message_type: 'text',
        content: message,
        direction: 'outbound',
        status: 'sent'
      })

      const result = await response.json()
      return {
        success: true,
        message: 'Message sent successfully',
        conversationId: this.conversationId!,
        ...result
      }

    } catch (error) {
      console.error('Failed to send message:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send message'
      }
    }
  }

  // Get message history for a user
  async getMessageHistory(userId: string, limit = 50): Promise<ChatMessage[]> {
    try {
      // Use dynamic query to avoid TypeScript compilation errors
      const { data, error } = await supabase
        .from('whatsapp_messages' as any)
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit)

      if (error) {
        console.warn('WhatsApp messages table not found, returning empty history:', error.message)
        return []
      }

      return ((data as unknown as ChatMessage[]) || []).reverse() // Return in chronological order
    } catch (error) {
      console.error('Failed to get message history:', error)
      return []
    }
  }

  // Get recent message history as text for context
  private async getRecentMessageHistory(userId: string, limit = 5): Promise<string> {
    try {
      const messages = await this.getMessageHistory(userId, limit)
      return messages
        .map(msg => `${msg.direction === 'inbound' ? 'Bot' : 'User'}: ${msg.content}`)
        .join('\\n')
    } catch {
      return ''
    }
  }

  // Set up real-time subscription for incoming messages
  private async setupRealtimeSubscription(): Promise<void> {
    if (!this.userId) return

    try {
      // Check if whatsapp_messages table exists first using dynamic query
      const { error: tableCheckError } = await supabase
        .from('whatsapp_messages' as any)
        .select('id')
        .limit(1)

      if (tableCheckError) {
        console.warn('WhatsApp messages table not found, skipping real-time subscription:', tableCheckError.message)
        return
      }

      // Set up subscription only if table exists
      supabase
        .channel('whatsapp_messages')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'whatsapp_messages',
            filter: `user_id=eq.${this.userId}`
          },
          (payload) => {
            const message = payload.new as ChatMessage
            if (message.direction === 'inbound') {
              // Notify listeners of new inbound message
              this.messageCallbacks.forEach(callback => callback(message))
            }
          }
        )
        .subscribe()
    } catch (error) {
      console.warn('Failed to set up real-time subscription:', error)
    }
  }

  // Get or create conversation for user
  private async getOrCreateConversation(userId: string): Promise<ChatConversation> {
    try {
      // Get user phone number first - use basic fields that exist
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('phone')
        .eq('id', userId)
        .single()

      // Allow temporary phone number for users without one
      const phoneNumber = (profile as any)?.phone || `temp-${userId}`

      // Check if whatsapp_conversations table exists using dynamic query
      const { data: existingConv, error: tableError } = await supabase
        .from('whatsapp_conversations' as any)
        .select('*')
        .eq('user_id', userId)
        .single()

      if (tableError && !tableError.message.includes('relation "whatsapp_conversations" does not exist')) {
        // Table exists but no conversation found, create one
        const { data: newConv } = await supabase
          .from('whatsapp_conversations' as any)
          .insert({
            user_id: userId,
            phone_number: phoneNumber,
            status: 'active'
          })
          .select()
          .single()

        return newConv as unknown as ChatConversation
      } else if (existingConv) {
        // Conversation exists
        return existingConv as unknown as ChatConversation
      }

      // Table doesn't exist, return mock conversation
      const mockConversation: ChatConversation = {
        id: `mock-conv-${userId}`,
        user_id: userId,
        phone_number: phoneNumber,
        status: 'active',
        last_message_at: new Date().toISOString(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }

      return mockConversation
    } catch (error) {
      console.error('Failed to get/create conversation:', error)
      // Return mock conversation on error
      return {
        id: `mock-conv-${userId}`,
        user_id: userId,
        phone_number: `temp-${userId}`,
        status: 'active',
        last_message_at: new Date().toISOString(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }
    }
  }

  // Store message in database
  private async storeMessage(messageData: Omit<ChatMessage, 'id' | 'created_at'>): Promise<void> {
    try {
      // Try to store message if table exists using dynamic query
      const { error } = await supabase
        .from('whatsapp_messages' as any)
        .insert({
          ...messageData,
          created_at: new Date().toISOString()
        })
      
      if (error && !error.message.includes('relation "whatsapp_messages" does not exist')) {
        throw error
      } else if (error) {
        console.warn('WhatsApp messages table not found, message not stored:', error.message)
      }
    } catch (error) {
      console.error('Failed to store message:', error)
      // Don't throw here as message sending should still succeed
    }
  }

  // Disconnect chat service
  async disconnect(): Promise<void> {
    this.connected = false
    this.userId = null
    this.conversationId = null
    this.messageCallbacks.clear()
    this.typingCallbacks.clear()
    
    // Remove Supabase subscriptions
    supabase.removeAllChannels()
  }

  // Register callback for incoming messages
  onMessage(callback: (message: ChatMessage) => void): void {
    this.messageCallbacks.add(callback)
  }

  // Remove message callback
  offMessage(callback: (message: ChatMessage) => void): void {
    this.messageCallbacks.delete(callback)
  }

  // Register callback for typing indicators (placeholder - not implemented in bot yet)
  onTyping(callback: (userId: string) => void): void {
    this.typingCallbacks.add(callback)
  }

  // Remove typing callback
  offTyping(callback: (userId: string) => void): void {
    this.typingCallbacks.delete(callback)
  }

  // Check if user is connected to WhatsApp bot
  async checkWhatsAppStatus(userId: string): Promise<{
    registered: boolean
    verified: boolean
    phoneNumber?: string
    referralCode?: string
  }> {
    try {
      // Use dynamic query to avoid TypeScript compilation errors
      const { data: profile, error } = await supabase
        .from('user_profiles' as any)
        .select('whatsapp_number, phone, referral_code, whatsapp_bot_registered')
        .eq('id', userId)
        .single()

      if (error) {
        // If columns don't exist, return safe defaults
        console.warn('WhatsApp columns not found in user_profiles:', error.message)
        return {
          registered: false,
          verified: false
        }
      }

      // Return actual values if columns exist
      return {
        registered: (profile as any)?.whatsapp_bot_registered || false,
        verified: false, // whatsapp_verified column not implemented yet
        phoneNumber: (profile as any)?.whatsapp_number || (profile as any)?.phone,
        referralCode: (profile as any)?.referral_code
      }
    } catch (error) {
      console.error('Failed to check WhatsApp status:', error)
      return {
        registered: false,
        verified: false
      }
    }
  }

  // Generate WhatsApp registration link
  generateWhatsAppLink(message?: string): string {
    const defaultMessage = 'Hi! I want to register for TyreLink.'
    const encodedMessage = encodeURIComponent(message || defaultMessage)
    return `https://wa.me/${BOT_CONFIG.phoneNumber.replace(/[^\d]/g, '')}?text=${encodedMessage}`
  }

  // Get bot admin dashboard URL
  getAdminUrl(): string {
    return BOT_CONFIG.adminUrl
  }
}

// Export singleton instance
export const chatService = new ChatService()

// Helper functions for component usage
export const useChatService = () => {
  return {
    chatService,
    connect: chatService.connect.bind(chatService),
    sendMessage: chatService.sendMessage.bind(chatService),
    getMessageHistory: chatService.getMessageHistory.bind(chatService),
    disconnect: chatService.disconnect.bind(chatService),
    onMessage: chatService.onMessage.bind(chatService),
    offMessage: chatService.offMessage.bind(chatService),
    checkWhatsAppStatus: chatService.checkWhatsAppStatus.bind(chatService),
    generateWhatsAppLink: chatService.generateWhatsAppLink.bind(chatService),
    getAdminUrl: chatService.getAdminUrl.bind(chatService)
  }
}
